import styled from 'styled-components';
import Highlight from 'react-highlight';
import { Field, useField } from 'formik';
import * as Yup from 'yup';
import { IconButton, Alert as MuiAlert } from '@mui/material';
import copy from 'copy-to-clipboard';
import { useTranslation } from 'react-i18next';

import 'highlight.js/styles/tomorrow-night.css';

import { Section, SectionTitle } from '..';
import { FormRowsDivider, InputField } from '../../../fields';
import { Typography } from '@mui/material';
import { MultiSelect } from '../../../fields/multiselect';
import { CONTACT_EMAIL } from '../../../../config';
import { ContentCopy } from '@mui/icons-material';
import { useCallback, useMemo } from 'react';
import { getUserPlan, useAppSelector, useUser } from '../../../../lib';
import { configSelectors } from '../../../../store';
import { AmoCRMIntegration } from './amocrm';
import { BitrixIntegration } from './bitrix';
import { RoistatIntegration } from './roistat';
import { GetResponseIntegration } from './get-response';
import { YaMetrikaIntegration } from './ya-metrika';
import { SendTestLead } from '../../send-test-lead';
import { GetCourseIntegration } from './get-course';

// prettier-ignore
const WEBHOOK_DESCRIPTION = `{
  // All fields are additionally duplicated in lowercase
  COMMENT: string;
  EMAIL: string;
  PHONE: string;
  CAMPAIGN: string;
  WIN: boolean;
  WIX_COUPON_ID: string;
  WIX_COUPON_NAME: string;
  FIRST_NAME: string;
  LAST_NAME: string;
  // meta data
  LOCATION: string;
  UTM_SOURCE: string;
  UTM_MEDIUM: string;
  UTM_CAMPAIGN: string;
  UTM_CONTENT: string;
  UTM_TERM: string;
  REFERRER: string;
  DATE: string;
  // roistat
  ROISTAT_VISIT?: string;
  ROISTAT_FIRST_VISIT?: string;
  // other form data
  [key: string]: string;
}
`;

type IntegrationOption = {
  id: string;
  label: string;
  forPaid?: boolean;
  disabled?: boolean;
};

export const External = () => {
  const [field] = useField<string[]>('campaign.integrations');
  const hasWebhookIntegration = field.value?.includes('webhook');
  const hasEmailIntegration = field.value?.includes('email');
  const hasAmoCRMIntegration = field.value?.includes('amocrm');
  const hasBitrixIntegration = field.value?.includes('bitrix');
  const hasRoistatIntegration = field.value?.includes('roistat');
  const hasGetResponseIntegration = field.value?.includes('get-response');
  const hasYaMetrikaIntegration = field.value?.includes('ya-metrika');
  const hasGetCourseIntegration = field.value?.includes('get-course');

  const { user } = useUser();
  const userPlan = getUserPlan(user);
  const { t } = useTranslation();
  const [{ value: webhookUrl }, { error: webhookUrlError }] = useField<string>('campaign.webhookUrl');
  const [{ value: campaignId }] = useField('campaign.id');

  const commonIntegrations: IntegrationOption[] = useMemo(
    () => [
      { id: 'webhook', label: t('integrations.webhook') },
      { id: 'email', label: t('integrations.email') },
    ],
    [t]
  );

  const standaloneIntegrations: IntegrationOption[] = useMemo(
    () => [
      ...commonIntegrations,
      {
        id: 'subheader',
        label: 'Только для платных тарифов',
      },
      {
        id: 'amocrm',
        label: 'AmoCRM',
        forPaid: true,
      },
      {
        id: 'bitrix',
        label: 'Bitrix24',
        forPaid: true,
      },
      {
        id: 'roistat',
        label: 'Roistat',
        forPaid: true,
      },
      {
        id: 'get-response',
        label: 'GetResponse',
        forPaid: true,
      },
      {
        id: 'get-course',
        label: 'GetCourse',
        forPaid: true,
      },
      {
        id: 'ya-metrika',
        label: 'Яндекс.Метрика',
        forPaid: true,
      },
    ],
    [commonIntegrations]
  );

  const isStandalone = useAppSelector(configSelectors.isStandalone);

  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const getPossibleIntegrations = useCallback(() => {
    let result = commonIntegrations;

    if (isStandalone) {
      result = standaloneIntegrations;
    }

    return result.map((option) => {
      if (option.forPaid && userPlan === 'free') {
        option.disabled = true;
      }

      return option;
    });
  }, [commonIntegrations, isStandalone, standaloneIntegrations, userPlan]);

  const integrations = useMemo(() => {
    return getPossibleIntegrations();
  }, [getPossibleIntegrations]);

  return (
    <DisplaySettingsWrapper>
      <Section elevation={0}>
        <SectionTitle>{t('pages.campaign.integrations.integrationsTitle')}</SectionTitle>

        <Row>
          <Field
            name="campaign.integrations"
            component={MultiSelect}
            label={t('integrations.add')}
            className="form-field"
            options={integrations}
          />
        </Row>

        {hasWebhookIntegration && (
          <>
            <FormRowsDivider />

            <Row column>
              <Field
                name="campaign.webhookUrl"
                component={InputField}
                label="Web hook url"
                className="form-field webhook-input"
                validate={(value: string) => validate(value, Yup.string().url('Should be an URL').nullable())}
              />

              {webhookUrl && !webhookUrlError && (
                <SendTestLead integrationId="webhook" campaignId={campaignId} params={{ webhookUrl }} />
              )}

              <Typography sx={{ marginTop: '12px' }} fontSize={14} component="span">
                {t('integrations.webhookDescription')}
              </Typography>

              <WebhookBodyExample>
                <Highlight className="typescript">{WEBHOOK_DESCRIPTION}</Highlight>
              </WebhookBodyExample>
            </Row>
          </>
        )}

        {hasEmailIntegration && (
          <>
            <FormRowsDivider />

            <Row>
              <Field
                name="campaign.emailsForNotifications"
                component={InputField}
                label={t('integrations.emails')}
                className="form-field webhook-input"
                validate={(value: string) => validate(value, Yup.string().nullable())}
                helperText={t('integrations.emailsDescription')}
                placeholder="mail_1@mail.com, mail_2@mail.com"
              />
            </Row>
          </>
        )}

        {hasAmoCRMIntegration && <AmoCRMIntegration />}
        {hasBitrixIntegration && <BitrixIntegration />}
        {hasRoistatIntegration && <RoistatIntegration />}
        {hasGetResponseIntegration && <GetResponseIntegration />}
        {hasYaMetrikaIntegration && <YaMetrikaIntegration />}
        {hasGetCourseIntegration && <GetCourseIntegration />}

        <Alert severity="info" variant="filled">
          {t('pages.campaign.integrations.integrationsInfo')}
          <EmailCopy
            onClick={() => {
              copy(CONTACT_EMAIL);
            }}
          >
            <Email>{CONTACT_EMAIL}</Email>
            <IconButton size="small">
              <ContentCopy fontSize="small" />
            </IconButton>
          </EmailCopy>
        </Alert>
      </Section>
    </DisplaySettingsWrapper>
  );
};

const DisplaySettingsWrapper = styled.div`
  .form-field {
    min-width: 290px;
  }

  .webhook-input {
    width: 530px;
    margin-bottom: 12px;
  }
`;

const Row = styled.div<{ noMargin?: boolean; column?: boolean }>`
  display: flex;

  ${(p) => p.column && `flex-direction: column;`};

  ${(p) => !p.noMargin && `margin-bottom: 24px;`}

  :last-of-type {
    ${(p) => !p.noMargin && `margin-bottom: 12px;`}
  }

  .send-test-lead-button {
    align-self: flex-start;
  }
`;

const WebhookBodyExample = styled.div`
  max-width: 530px;
  border-radius: 4px;
  font-size: 14px;
`;

const Alert = styled(MuiAlert)`
  display: flex;
  align-items: center;
  margin-top: 24px;
  font-weight: 400;
`;

const Email = styled.div`
  font-weight: 500;
  cursor: pointer;
  margin-right: 4px;
`;

const EmailCopy = styled.div`
  display: flex;
  align-items: center;
`;
